import type { PaymentRetry } from '@/features/paymentRetries';
import { Pagination } from '@/shared/components/Pagination';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import { formatDate } from '@/shared/utils/date';
import {
  CAlert,
  CBadge,
  CButton,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react';
import React from 'react';
import { getStatusColor } from './utils';

interface CustomerPaymentRetriesTableProps {
  paymentRetries: PaymentRetry[];
  loading: boolean;
  error: string | null;
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onCancel?: (paymentRetry: PaymentRetry) => void;
}

export const CustomerPaymentRetriesTable: React.FC<CustomerPaymentRetriesTableProps> = ({
  paymentRetries,
  loading,
  error,
  page,
  perPage,
  total,
  totalPages,
  onPageChange,
  onPerPageChange,
  onCancel,
}) => {
  const { hasPermission } = usePermissions();
  const canCancel = hasPermission(USER_PERMISSIONS.CustomersPaymentRetriesCancel);

  if (loading) {
    return <div className="text-center">Loading...</div>;
  }

  return (
    <>
      {error && <CAlert color="danger">{error}</CAlert>}
      {!error && (
        <>
          {paymentRetries.length > 0 ? (
            <>
              <CTable hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Status</CTableHeaderCell>
                    <CTableHeaderCell>Shopify Customer ID</CTableHeaderCell>
                    <CTableHeaderCell>Created At</CTableHeaderCell>
                    <CTableHeaderCell>Updated At</CTableHeaderCell>
                    {canCancel && <CTableHeaderCell>Actions</CTableHeaderCell>}
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {paymentRetries.map((paymentRetry: PaymentRetry) => (
                    <CTableRow key={paymentRetry.id}>
                      <CTableDataCell>
                        <CBadge color={getStatusColor(paymentRetry.status)}>
                          {paymentRetry.status}
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>{paymentRetry.shopifyCustomerId}</CTableDataCell>
                      <CTableDataCell>{formatDate(paymentRetry.createdAt)}</CTableDataCell>
                      <CTableDataCell>{formatDate(paymentRetry.updatedAt)}</CTableDataCell>
                      {canCancel && (
                        <CTableDataCell>
                          {['pending', 'processing'].includes(paymentRetry.status) && onCancel ? (
                            <CButton
                              color="danger"
                              size="sm"
                              onClick={() => onCancel(paymentRetry)}
                            >
                              Cancel
                            </CButton>
                          ) : (
                            '-'
                          )}
                        </CTableDataCell>
                      )}
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
              <div className="d-flex justify-content-center mt-3">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  perPage={perPage}
                  total={total}
                  onPageChange={onPageChange}
                  onPerPageChange={onPerPageChange}
                />
              </div>
            </>
          ) : (
            <CAlert color="info">No payment retries found for this customer.</CAlert>
          )}
        </>
      )}
    </>
  );
};
